import React__default, { useContext, useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo, forwardRef } from 'react';
import { E as ErrorBoundary, c as classNames } from './vendor-9438ce5c.js';
import { createUseStyles, useTheme } from 'react-jss';
import { defineMessages, t, L10NContext, T } from '@sm/intl';
import { sanitizeString } from '@sm/utils';
import { uniqueId, visuallyHidden } from '@wds/utils';
import ReactDOM from 'react-dom';
import unescaper from 'lodash.unescape';
const useStyles$h = createUseStyles(theme => ({
  wdsIcons: ({
    color,
    size
  }) => {
    var _a, _b, _c;

    const fontSize = size ? {
      fontSize: theme.iconSize[size]
    } : {
      fontSize: 'inherit'
    };
    const chosenColor = // @ts-expect-error Fallback styles are acceptable
    ((_a = theme.palette) === null || _a === void 0 ? void 0 : _a.text[color]) || ((_c = (_b = theme.palette) === null || _b === void 0 ? void 0 : _b[color]) === null || _c === void 0 ? void 0 : _c.main) || 'currentColor';
    return { ...fontSize,
      width: '1em',
      height: '1em',
      display: 'inline-block',
      verticalAlign: 'middle',
      textAlign: 'center',
      fill: chosenColor
    };
  }
})); // https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging

const getDisplayName = Component => Component.displayName || Component.name || 'Component';

function withIcon(Component) {
  function WithIcon({
    color,
    title,
    size,
    ...rest
  }) {
    const classes = useStyles$h({
      color,
      size
    });
    return React__default.createElement("svg", {
      className: classes.wdsIcons,
      viewBox: "0 0 16 16",
      preserveAspectRatio: "xMidYMid",
      "aria-label": title,
      role: title ? 'img' : 'presentation',
      ...rest
    }, React__default.createElement(Component, null));
  }

  WithIcon.displayName = `WithIcon(${getDisplayName(Component)})`;

  WithIcon.getOriginalComponent = () => Component;

  return WithIcon;
}

createUseStyles({
  svgContainer: {
    display: 'inline-block',
    width: '1em',
    height: '1em'
  }
});

function DocumentXIconPath() {
  return React__default.createElement("path", {
    d: "m5.94.005 8.012.012C14.53.018 15 .487 15 1.067V14.95c0 .58-.47 1.05-1.05 1.05H2.05C1.47 16 1 15.53 1 14.95V4.94L5.94.004zM7 1.505V4.95c0 .541-.41.987-.936 1.044L5.95 6H2.5v8.5h11V1.514L7 1.505zM6.557 7.53 8 8.973 9.443 7.53a.765.765 0 0 1 1.083 1.082l-1.444 1.443 1.444 1.444a.765.765 0 0 1-1.083 1.082L8 11.138 6.557 12.58A.765.765 0 1 1 5.474 11.5l1.444-1.444-1.444-1.443A.765.765 0 1 1 6.557 7.53zM5.5 2.563 3.561 4.5H5.5V2.562z"
  });
}

var IconDocumentX = withIcon(DocumentXIconPath);
const useStyles$g = createUseStyles(theme => {
  var _a, _b;

  return {
    fallbackComponent: {
      fontFamily: (_b = (_a = theme.questionBody) === null || _a === void 0 ? void 0 : _a.fontFamily) !== null && _b !== void 0 ? _b : {},
      color: theme.questionColor,
      border: `1px solid ${theme.questionColor}`,
      borderRadius: '5px',
      width: '100%',
      height: '100%',
      minHeight: '220px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& svg': {
        fill: theme.questionColor
      }
    },
    errorMessage: {
      marginLeft: `8px` // equivalent to wrench theme.spacing[2]

    }
  };
});
const COPY$4 = defineMessages({
  ERROR: {
    id: 'FallbackComponent.ERROR',
    defaultMessage: 'Error loading question',
    description: '[Type: label][Vis: high] - error message indicating a question could not be loaded'
  }
});

function FallbackComponent() {
  const {
    fallbackComponent,
    errorMessage
  } = useStyles$g();
  return React__default.createElement("div", {
    className: fallbackComponent
  }, React__default.createElement(IconDocumentX, null), React__default.createElement("p", {
    className: errorMessage
  }, t(COPY$4.ERROR)));
}

const withErrorBoundary = Component => {
  const displayName = Component.displayName || Component.name || 'Component';

  function ComponentWithErrorBoundary(props) {
    return React__default.createElement(ErrorBoundary, {
      FallbackComponent: FallbackComponent
    }, React__default.createElement(Component, { ...props
    }));
  }

  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;
  return ComponentWithErrorBoundary;
};

const useStyles$f = createUseStyles(theme => {
  var _a, _b;

  return {
    tooltip: {
      'span[data-tooltip] &': {
        display: 'none'
      },
      [`@media (min-width: ${(_b = (_a = theme.breakpoints) === null || _a === void 0 ? void 0 : _a.md.min) !== null && _b !== void 0 ? _b : '769px'})`]: {
        'span[data-tooltip]:hover &, span[data-tooltip]:focus &': {
          display: 'inline-block'
        }
      },
      position: 'absolute',
      left: ({
        center
      }) => center,
      bottom: 20,
      width: 275,
      transform: 'translate(-50%, -5px)',
      textAlign: 'center',
      zIndex: 12,
      fontSize: 13,
      '&:after': {
        position: 'absolute',
        bottom: -4,
        left: '50%',
        width: 10,
        height: 10,
        backgroundColor: 'white',
        content: '""',
        marginLeft: -5,
        transform: 'rotate(45deg)',
        boxShadow: [[3, 5, 5, 'rgb(0 0 0 / 0.07)']],
        zIndex: 13
      }
    },
    tooltipBody: {
      display: 'inline-block',
      maxWidth: 275,
      color: 'black',
      fontWeight: 300,
      fontFamily: theme.fontFamily,
      fontStyle: 'normal',
      boxShadow: [[0, 2, 10, 0, 'rgb(0 0 0 / 0.14)']],
      background: 'white',
      padding: [[12, 16]],
      lineHeight: 1.4,
      borderRadius: 2,
      textAlign: 'left'
    }
  };
});
const defaultSelectors = ['a[href]', 'button', 'textarea', 'input:not([type="hidden"])', 'select', 'details', '[tabindex]:not([tabindex="-1"])'];

const useFocusTrap = ({
  selectors = defaultSelectors
} = {}) => {
  const focusRef = React__default.useRef(null);
  const [focusableEls, setEls] = React__default.useState([]);
  React__default.useEffect(() => {
    var _a;

    setEls(Array.from((_a = focusRef === null || focusRef === void 0 ? void 0 : focusRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(selectors.join(','))));
  }, [focusRef, selectors]);

  function handleFocus(e) {
    if (focusRef.current) {
      const focusableElements = focusableEls;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  }

  React__default.useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Tab') {
        handleFocus(e);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });
  return focusRef;
};

const useStyles$e = createUseStyles(theme => {
  var _a;

  return {
    modalOverlay: {
      zIndex: 999,
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end'
    },
    modal: {
      position: 'relative',
      width: '85vw',
      minHeight: '20vw',
      marginBottom: 20,
      background: 'white',
      borderRadius: 2
    },
    closeBtn: {
      position: 'absolute',
      cursor: 'pointer',
      background: 'transparent',
      right: 0,
      border: 0,
      padding: 8,
      marginTop: 0,
      color: 'black',
      zIndex: 105
    },
    tooltipText: {
      color: ((_a = theme.questionBody) === null || _a === void 0 ? void 0 : _a.color) || '#333E48',
      fontFamily: theme.fontFamily,
      fontSize: 13,
      fontWeight: 400,
      lineHeight: '1.4',
      padding: [28, 12, 16]
    }
  };
});
const COPY$3 = defineMessages({
  CLOSE_MODAL: {
    id: 'Modal.CloseModal',
    defaultMessage: 'Close tooltip',
    description: '[Type: label][Vis: Med] - close button text'
  }
});

function Modal({
  tooltip,
  closeModal
}) {
  const {
    modalOverlay,
    closeBtn,
    modal,
    tooltipText
  } = useStyles$e();
  const closeRef = React__default.useRef(null);
  const tooltipId = uniqueId('tooltip-modal');
  const focusRef = useFocusTrap();

  const handleClick = e => {
    // click on button or overlay should close modal
    e.stopPropagation();

    if (e.currentTarget === e.target) {
      closeModal();
    }
  };

  React__default.useEffect(() => {
    // listen for ESC to close modal
    const handleKeydown = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [closeModal]);
  React__default.useEffect(() => {
    var _a;

    (_a = closeRef.current) === null || _a === void 0 ? void 0 : _a.focus();
  }, []);
  return React__default.createElement("div", {
    className: modalOverlay,
    onClick: handleClick,
    "aria-hidden": "true"
  }, React__default.createElement("div", {
    ref: focusRef,
    className: modal,
    role: "dialog",
    "aria-modal": "true",
    "aria-describedby": tooltipId
  }, React__default.createElement("button", {
    type: "button",
    className: closeBtn,
    ref: closeRef,
    onClick: handleClick,
    "aria-label": t(COPY$3.CLOSE_MODAL)
  }, "\u00D7"), React__default.createElement("p", {
    id: tooltipId,
    className: tooltipText
  }, tooltip)));
}
/** Displays a tooltip on hover for large screens and a modal on click for small screens */


const Tooltip = ({
  container,
  className,
  id = ''
}) => {
  var _a;
  /** container center position to display tooltip */


  const center = container.offsetWidth / 2;
  const text = (_a = container.getAttribute('data-tooltip')) !== null && _a !== void 0 ? _a : '';
  const theme = useTheme();
  const {
    tooltip,
    tooltipBody
  } = useStyles$f({
    center
  });
  const [showModal, setShowModal] = React__default.useState(false);
  const [mode, setMode] = React__default.useState('TOOLTIP');

  const closeModal = () => {
    setShowModal(false);
    container.focus();
  };

  React__default.useEffect(() => {
    container.setAttribute('role', 'tooltip'); // make tooltip anchor focusable

    if (!container.hasAttribute('tabIndex')) {
      container.setAttribute('tabIndex', '0');
    } // opens modal if matches small screen breakpoint


    const openModal = () => {
      var _a, _b;

      const mql = window.matchMedia(`(max-width: ${(_b = (_a = theme === null || theme === void 0 ? void 0 : theme.breakpoints) === null || _a === void 0 ? void 0 : _a.md.min) !== null && _b !== void 0 ? _b : '769px'})`);

      if (mql.matches) {
        setMode('MODAL');
        setShowModal(true);
      } else {
        setMode('TOOLTIP');
      }
    };

    const handleClick = () => {
      openModal();
    };

    const handleKeydown = e => {
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        openModal();
      }
    }; // listen for click, ENTER or SPACE to open modal


    container.addEventListener('click', handleClick);
    container.addEventListener('keydown', handleKeydown);
    return () => {
      container.removeEventListener('click', handleClick);
      container.removeEventListener('keydown', handleKeydown);
    };
  }, [container, theme]);
  const modalEl = showModal ? React__default.createElement(Modal, {
    tooltip: text,
    closeModal: closeModal
  }) : null;
  const tooltipEl = React__default.createElement("div", {
    className: classNames(tooltip, className)
  }, React__default.createElement("span", {
    id: id,
    className: tooltipBody,
    "aria-hidden": "true"
  }, text));
  const child = mode === 'MODAL' ? modalEl : tooltipEl;
  const containerEl = mode === 'MODAL' ? document.body : container;
  return ReactDOM.createPortal(child, containerEl);
};

const listSpacing = {
  margin: [16, 0],
  padding: [0, 0, 0, 40]
};
const mediaReset = {
  border: 0,
  maxWidth: '100%'
};
const useStyles$d = createUseStyles({
  richTextContent: {
    // Typography
    '& a': {
      // Links are not themed by default. Users must select a link color in the editor
      color: 'blue',
      textDecoration: 'underline',
      '&:hover': {
        cursor: 'pointer'
      },
      '&:focus': {
        outline: 'currentColor auto 1px'
      }
    },
    '& ol': { ...listSpacing,
      listStyleType: 'decimal'
    },
    '& ul': { ...listSpacing,
      listStyle: 'disc outside none',
      '& ul li': {
        listStyle: 'circle'
      }
    },
    '& p': {
      margin: [13, 0]
    },
    '& b': {
      fontWeight: 'bold'
    },
    '& strong': {
      fontWeight: 'bold'
    },
    '& em': {
      fontStyle: 'italic'
    },
    '& i': {
      fontStyle: 'italic'
    },
    '& big': {
      fontSize: 'larger'
    },
    '& small': {
      fontSize: 'smaller'
    },
    '& sup': {
      fontSize: '75%',
      lineHeight: 0,
      position: 'relative',
      top: '-0.5em',
      verticalAlign: 'baseline'
    },
    // Media embeds
    '& iframe': mediaReset,
    '& img': { ...mediaReset,
      height: 'auto',
      '-ms-interpolation-mode': 'bicubic'
    },
    '& span[data-tooltip]': {
      position: 'relative',
      display: 'inline',
      borderBottom: [[1, 'dashed']],
      textDecoration: 'inherit',
      cursor: 'pointer'
    }
  }
}); // Support iframe video embeds and their matching attributes

const sanitizeOptions = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'sandbox', 'scrolling']
};

function RichText({
  id: elementId,
  element: Element = 'div',
  text,
  className,
  ...containerProps
}) {
  const {
    richTextContent
  } = useStyles$d();
  const [tooltipNodes, setTooltipNodes] = React__default.useState([]);
  const tooltipRef = React__default.useRef(null);
  React__default.useEffect(() => {
    if (tooltipRef === null || tooltipRef === void 0 ? void 0 : tooltipRef.current) {
      const el = tooltipRef === null || tooltipRef === void 0 ? void 0 : tooltipRef.current;
      setTooltipNodes(Array.from(el.querySelectorAll('[data-tooltip]')));
    }
  }, []);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Element, {
    id: elementId,
    className: classNames(richTextContent, className),
    dangerouslySetInnerHTML: {
      __html: sanitizeString(text, sanitizeOptions)
    },
    ref: tooltipRef,
    translate: "no",
    ...containerProps
  }), tooltipNodes.map(container => {
    const id = uniqueId('rich-text-tooltip');
    container.setAttribute('aria-describedby', id);
    return React__default.createElement(Tooltip, {
      key: id,
      container: container,
      id: id
    });
  }));
}

const textReset = {
  margin: 0
};
const useStyles$c = createUseStyles(theme => {
  const {
    fontSize,
    ...questionTitleFormatting
  } = theme.questionTitle;
  const questionTitleFontSize = fontSize !== null && fontSize !== void 0 ? fontSize : {};
  return {
    addonContainer: {
      display: 'inline-block',
      margin: [0, 5],
      verticalAlign: 'text-bottom'
    },
    container: {
      position: 'relative'
    },
    questionNumber: ({
      isRTL
    }) => ({
      margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0'
    }),
    questionTitle: ({
      useDefaultFrontSize
    }) => {
      var _a;

      return { ...textReset,
        ...questionTitleFormatting,
        color: ((_a = theme.questionTitle) === null || _a === void 0 ? void 0 : _a.color) || theme.questionColor,
        fontSize: useDefaultFrontSize && !theme.isAccessible ? theme.fontSize.body : questionTitleFontSize,
        lineHeight: 1.25,
        whiteSpace: 'normal',
        wordWrap: 'break-word'
      };
    },
    requiredAsterisk: ({
      isRTL
    }) => ({
      display: 'inline-block',
      margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0'
    })
  };
});

function RespondentQuestionTitle({
  element: Element = 'div',
  addon,
  heading,
  id,
  number,
  numbered,
  required,
  useDefaultFrontSize = false,
  ...props
}) {
  const {
    isRTL
  } = useContext(L10NContext);
  const {
    addonContainer,
    container,
    questionNumber,
    questionTitle,
    requiredAsterisk
  } = useStyles$c({
    useDefaultFrontSize,
    isRTL
  });
  return React__default.createElement(Element, {
    id: id,
    className: classNames(container, questionTitle),
    translate: "no",
    ...props
  }, required && React__default.createElement("span", {
    "aria-hidden": true,
    className: requiredAsterisk
  }, "*"), numbered && React__default.createElement("span", {
    className: questionNumber
  }, number, "."), React__default.createElement(RichText, {
    element: "span",
    text: heading
  }), addon && React__default.createElement("span", {
    className: addonContainer
  }, addon));
}

createUseStyles(theme => {
  var _a;

  return {
    button: {
      color: theme.nextButton.color,
      backgroundColor: theme.nextButton.backgroundColor,
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontSize: 15,
      border: [1, 'solid', 'transparent'],
      borderRadius: 2,
      padding: [1, 6],
      maxWidth: '100%',
      minWidth: 60,
      minHeight: 40,
      cursor: 'pointer',
      overflowWrap: 'break-word',
      transition: `all .2s linear`,
      '&:focus, &:hover': {
        outlineOffset: 5,
        outline: `1px solid ${theme.answerColor}`,
        // Safari seems to have an outline bug, may not render without boxshadow
        boxShadow: '0 0 4px transparent'
      }
    }
  };
});
defineMessages({
  OK_LABEL: {
    id: 'Button.OK_LABEL',
    defaultMessage: 'OK',
    description: '[Type: button][Vis: high] - question ok label'
  }
});
/** Referentially stable empty function
 *
 * e.g. as default or placeholder */
// eslint-disable-next-line @typescript-eslint/no-empty-function

const EmptyFn = () => {};
/* eslint-disable no-param-reassign, no-plusplus */

/**
 * @name EasingFunctions- Useful easing equations
 * @comment only considering the t value for the range [0, 1] => [0, 1]
 */


const easingFunctions = {
  /** @comment linear - no easing, no acceleration */
  linear: t => t,

  /** @comment easeInQuad - accelerating from zero velocity */
  easeInQuad: t => t * t,

  /** @comment easeOutQuad - decelerating to zero velocity */
  easeOutQuad: t => t * (2 - t),

  /** @comment easeInOutQuad - acceleration until halfway, then deceleration */
  easeInOutQuad: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,

  /** @comment easeInCubic - accelerating from zero velocity */
  easeInCubic: t => t * t * t,

  /** @comment easeOutCubic - decelerating to zero velocity */
  easeOutCubic: t => --t * t * t + 1,

  /** @comment easeInOutCubic - acceleration until halfway, then deceleration */
  easeInOutCubic: t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,

  /** @comment easeInQuart - accelerating from zero velocity */
  easeInQuart: t => t * t * t * t,

  /** @comment easeOutQuart - decelerating to zero velocity */
  easeOutQuart: t => 1 - --t * t * t * t,

  /** @comment easeInOutQuart - acceleration until halfway, then deceleration */
  easeInOutQuart: t => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t,

  /** @comment easeInQuint - accelerating from zero velocity */
  easeInQuint: t => t * t * t * t * t,

  /** @comment easeOutQuint - decelerating to zero velocity */
  easeOutQuint: t => 1 + --t * t * t * t * t,

  /** @comment easeInOutQuint - acceleration until halfway, then deceleration */
  easeInOutQuint: t => t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t
};
/**
 * Given a start/end point of a scroll and time elapsed, calculate the scroll position we should be at
 */

function getValue(start, end, elapsed, duration, easingType = 'linear') {
  if (elapsed > duration) {
    return end;
  }

  const easing = typeof easingType === 'string' ? easingFunctions[easingType] : easingType;
  return start + (end - start) * easing(elapsed / duration);
}
/**
 * Smoothly animate between two values
 */


function animate({
  fromValue,
  toValue,
  onStart,
  onUpdate,
  onComplete,
  duration = 600,
  delay = 0,
  easingType = 'easeOutQuart'
}) {
  const startTime = performance.now() + delay;

  const tick = () => {
    const elapsed = performance.now() - startTime;
    const time = getValue(fromValue, toValue, elapsed, duration, easingType);
    const callback = elapsed <= duration ? tick : onComplete !== null && onComplete !== void 0 ? onComplete : EmptyFn;

    const updateAnimation = () => {
      onUpdate(time, callback);
    };

    window.requestAnimationFrame(updateAnimation);
  };

  onStart === null || onStart === void 0 ? void 0 : onStart();

  if (delay > 0) {
    setTimeout(() => {
      tick();
    }, delay);
  } else {
    tick();
  }
}

const useStyles$b = createUseStyles({
  visuallyHidden: visuallyHidden
});

function VisuallyHidden({
  children,
  element: Element
}) {
  const {
    visuallyHidden
  } = useStyles$b();
  return React__default.createElement(Element, {
    className: visuallyHidden
  }, children);
}

const useStyles$a = createUseStyles({
  paddingBox: ({
    padding,
    width
  }) => {
    return {
      paddingTop: (padding === null || padding === void 0 ? void 0 : padding.top) || 0,
      paddingBottom: (padding === null || padding === void 0 ? void 0 : padding.bottom) || 0,
      paddingLeft: (padding === null || padding === void 0 ? void 0 : padding.left) || 0,
      paddingRight: (padding === null || padding === void 0 ? void 0 : padding.right) || 0,
      width: width ? `${width.width}${width.format === 'PERCENT' ? '%' : 'px'}` : '100%',
      maxWidth: '100%'
    };
  }
});

function QuestionSpacing({
  padding,
  width,
  children,
  ...props
}) {
  const {
    paddingBox
  } = useStyles$a({
    padding,
    width
  });
  return React__default.createElement("div", {
    className: paddingBox,
    "data-testid": "QuestionSpacing__paddingBox",
    ...props
  }, children);
}
/**
 * Hook that calls the provided callback function when the ref element is blurred,
 * only if the next focus is outside the ref element
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * useOnFocusLeave(ref, ()=> onLeaveQuestion());
 * <div rf={ref}><input /></div>
 */


function useOnFocusLeave(ref, focusCallback) {
  useEffect(() => {
    const ele = ref === null || ref === void 0 ? void 0 : ref.current;

    function handleFocus(e) {
      if (!(ele === null || ele === void 0 ? void 0 : ele.contains(e.relatedTarget))) {
        focusCallback(ref);
      }
    }

    ele === null || ele === void 0 ? void 0 : ele.addEventListener('focusout', handleFocus);
    return () => {
      ele === null || ele === void 0 ? void 0 : ele.removeEventListener('focusout', handleFocus);
    };
  }, [ref, focusCallback]);
}

const createFieldId = questionId => `question-field-${questionId}`;

const createLegendId = questionId => `question-title-legend-${questionId}`;

const createErrorRowId = questionId => `error-row-${questionId}`;

const useStyles$9 = createUseStyles({
  footerRow: ({
    hasFooterPadding
  }) => ({
    paddingTop: hasFooterPadding ? 20 : 0
  }),
  containerStyles: {
    position: 'relative',
    transition: 'opacity 500ms ease-out'
  },
  errorRow: {
    overflow: 'hidden'
  },
  formReset: {
    border: 0,
    minWidth: 0
  },
  headerRow: {
    marginBottom: 24,
    width: '100%'
  }
});
const COPY$2 = defineMessages({
  QUESTION_TITLE: {
    id: 'QuestionFieldLayoutTemplate.QUESTION_TITLE',
    defaultMessage: 'Question Title',
    description: '[Type: Label][Vis: low] - question landmark for screen readers only'
  }
});

function QuestionFieldLayoutTemplate({
  footer,
  hasFooterPadding,
  children,
  clickShield,
  error,
  id,
  padding,
  width = {
    width: 100,
    format: 'PERCENT'
  },
  title,
  onSubmit,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onLeave = () => {},
  ...props
}) {
  const {
    footerRow,
    containerStyles,
    formReset,
    errorRow,
    headerRow
  } = useStyles$9({
    hasFooterPadding,
    clickShield
  });
  const containerRef = useRef(null);
  const errorRef = useRef(null);
  /** Keep track of last error to enable error-hiding animation */

  const [lastShownError, setLastShownError] = useState(error);
  const handleKeydown = useCallback(event => {
    if (event.key === 'Enter') {
      onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit();
    }
  }, [onSubmit]);
  /**
   * resolves double height animate issue with RAWR-1240
   */

  const isAnimating = useRef(false);
  useLayoutEffect(() => {
    const el = errorRef.current;

    if (!el || isAnimating.current) {
      return;
    }

    const hasError = !!error;
    const initialHeight = el.offsetHeight;
    el.style.height = 'auto'; // remove height to measure full size

    const {
      offsetHeight
    } = el;
    const fromValue = hasError ? 0 : offsetHeight;
    const toValue = hasError ? offsetHeight : 0; // don't animate if value is already correct (e.g. at initial load)

    if (initialHeight === toValue) {
      el.style.height = `${initialHeight}px`;
      return;
    }

    el.style.height = `${initialHeight}px`;
    animate({
      fromValue,
      toValue,
      duration: 300,
      delay: 200,
      onUpdate: (height, next) => {
        el.style.height = `${height}px`;
        next();
      },
      onStart: () => {
        isAnimating.current = true;
      },
      onComplete: () => {
        setLastShownError(error);
        isAnimating.current = false;
      }
    });
  }, [error]);
  const fieldsetRef = useRef(null);
  useOnFocusLeave(fieldsetRef, onLeave);
  useEffect(() => {
    const {
      current: container
    } = containerRef;
    container === null || container === void 0 ? void 0 : container.addEventListener('keydown', handleKeydown);
    return () => {
      return container === null || container === void 0 ? void 0 : container.removeEventListener('keydown', handleKeydown);
    };
  }, [containerRef, handleKeydown]);
  /**
   * This id is queried in RespWeb to calculate the offset for scroll-to-error animations in classic mode
   * ref: https://code.corp.surveymonkey.com/webplatform/smweb/pull/10725
   */

  let errorRowId;
  /** used in RespWeb for OQAATView */

  let fieldId;
  /** used in RespWeb for OQAATView */

  let questionTitleId;

  if (id) {
    errorRowId = createErrorRowId(id);
    fieldId = createFieldId(id);
    questionTitleId = createLegendId(id);
  }

  return React__default.createElement("div", {
    ref: containerRef,
    id: fieldId,
    ...props,
    className: containerStyles
  }, React__default.createElement(VisuallyHidden, {
    element: "div"
  }, React__default.createElement(T, {
    desc: COPY$2.QUESTION_TITLE
  })), React__default.createElement(QuestionSpacing, {
    padding: padding,
    width: width
  }, React__default.createElement("div", {
    ref: errorRef,
    id: errorRowId,
    className: errorRow
  }, error || lastShownError), React__default.createElement("fieldset", {
    ref: fieldsetRef,
    className: formReset
  }, React__default.createElement("legend", {
    id: questionTitleId,
    className: headerRow,
    tabIndex: -1
  }, title), children), footer && React__default.createElement("div", {
    className: footerRow
  }, footer)));
}
/**
 * Calculate the max number of children each column can be filled in the layout
 * @param columns number of columns
 * @param total total child count
 * @description Based on the current implementation in production (as of Jan 20, 2021),
 * - if columns = 1, the max number of children per column = total (render all children vertically)
 * - if columns > 1, the max number of children per column = (total / columns) + 1
 */


const calculateMaxNumOfChildrenPerColumn = (columns, total) => {
  if (columns === 1) {
    return total;
  }

  const childrenPerColumn = total / columns;

  if (childrenPerColumn % 1 === 0) {
    return childrenPerColumn;
  }

  return Math.floor(childrenPerColumn) + 1;
};
/**
 * Divide the children array into slices for rendering
 * @param childrenArr - children array
 * @param maxCellsPerCol - maximum number of children per slice
 * @param totalColumns - number of columns
 * @example
 * childrenArr = [option1, option2, option3, option4, option5, option6, option7]
 * childrenPerSlice = 2
 * totalColumns = 3
 * output: [[option1, option2, option3], [option4, option5], [option6, option7]]
 */


const sliceChildren = (childrenArr, maxCellsPerCol, totalColumns) => {
  let index = 0;
  const result = [];
  const partialColumnsCount = totalColumns * maxCellsPerCol - childrenArr.length; // Calculate the number of fullColumns, to conditionally push slice with the correct # of cells

  let fullColumnsCount = totalColumns - partialColumnsCount;

  while (index < childrenArr.length) {
    if (fullColumnsCount > 0) {
      result.push(childrenArr.slice(index, index + maxCellsPerCol));
      index += maxCellsPerCol;
    } else {
      result.push(childrenArr.slice(index, index + maxCellsPerCol - 1));
      index += maxCellsPerCol - 1;
    }

    fullColumnsCount -= 1;
  }

  return result;
};

const useStyles$8 = createUseStyles(theme => ({
  containerVertical: {
    display: 'initial'
  },
  containerHorizontal: {
    display: 'block'
  },

  /** shared between horizontal and vertical columns */
  column: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },
  columnHorizontal: {},

  /** Horizontal Column that adjust width to content width */
  columnHorizontalAutoAdjust: {
    width: 'auto'
  },
  [`@media (min-width: ${theme.breakpoints.xs.min})`]: {
    containerVertical: {
      display: 'flex'
    },
    containerHorizontal: {
      display: 'flex',
      flexWrap: 'wrap'
    }
  },
  [`@media only screen and (min-width: ${theme.breakpoints.lg.min})`]: {
    columnHorizontal: {
      width: '19%'
    }
  },
  [`@media (min-width: ${theme.breakpoints.sm.min}) and (max-width: ${theme.breakpoints.md.max})`]: {
    columnHorizontal: {
      width: '24%'
    }
  },
  [`@media only screen and (max-width: ${theme.breakpoints.xxs.max})`]: {
    columnHorizontal: {
      width: '100%',
      display: 'block'
    },
    columnHorizontalAutoAdjust: {
      width: '100%',
      display: 'block'
    }
  },
  answerLayoutCell: ({
    gridCellMargin = '0'
  }) => ({
    margin: gridCellMargin,
    flex: [0, 0, 'auto'],
    wordBreak: 'break-all'
  }),
  otherLayoutCell: ({
    otherCellMargin = '0'
  }) => ({
    margin: otherCellMargin
  })
}));

function QuestionAnswerLayoutTemplate({
  children,
  columns = 1,
  other,
  noneOfTheAbove,
  adjustToContent = false,
  gridCellMargin,
  otherCellMargin
}) {
  const {
    containerVertical,
    containerHorizontal,
    column,
    columnHorizontal,
    columnHorizontalAutoAdjust,
    answerLayoutCell,
    otherLayoutCell
  } = useStyles$8({
    columns,
    adjustToContent,
    gridCellMargin,
    otherCellMargin
  });
  const isHorizontal = columns === 'horizontal';
  /* Based on the columns config, divide the question answers
    (children of this component) into column groups for rendering.
       The reason we need to do this is when there are multiple columns,
    the answers are listed vertically instead of horizontally.
    For example:
    If there are 3 answers and 2 columns,
    Then the ordering will be:
    Answer 1    Answer 3
    Answer 2
       NOT:
    Answer 1    Answer 2
    Answer 3
    * though `horizontal` uses this
    
  */

  const columnGroups = useMemo(() => {
    const totalChildren = React__default.Children.count(children);
    const totalColumns = columns === 'horizontal' ? totalChildren : columns;
    const childrenArray = React__default.Children.toArray(children);

    if (!other && !!noneOfTheAbove && columns !== 'horizontal') {
      childrenArray.push(noneOfTheAbove);
    }

    const answersPerColumn = columns === 'horizontal' ? 1 : calculateMaxNumOfChildrenPerColumn(totalColumns, totalChildren + (!other && !!noneOfTheAbove ? 1 : 0));
    return sliceChildren(childrenArray, answersPerColumn, totalColumns);
  }, [children, columns, noneOfTheAbove, other]);
  const columnClassNames = isHorizontal ? [column, adjustToContent ? columnHorizontalAutoAdjust : columnHorizontal] : column;
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    className: isHorizontal ? containerHorizontal : containerVertical
  }, columnGroups.map((col, columnIndex) => React__default.createElement("div", {
    key: `col-${columnIndex + 1}`,
    className: classNames(columnClassNames),
    "data-testid": "answer-layout-column"
  }, col.map((cell, cellIndex) => React__default.createElement("div", {
    key: `cell-${cellIndex + 1}`,
    "data-testid": "answer-layout-cell",
    className: answerLayoutCell
  }, cell))))), other && React__default.createElement("div", {
    className: otherLayoutCell
  }, other), (other && noneOfTheAbove || columns === 'horizontal') && React__default.createElement("div", {
    className: otherLayoutCell,
    "data-testid": "Other-NOTA"
  }, noneOfTheAbove));
}

createUseStyles(theme => {
  var _a;

  return {
    icon: {
      color: '#F05B24',
      fontSize: theme.fontSize.body,
      alignSelf: 'flex-start',
      marginTop: 2,
      flexShrink: 0
    },
    validationMessage: {
      fontFamily: theme.fontFamily,
      lineHeight: 1.5,
      color: '#333e48',
      fontWeight: 400,
      fontSize: 16,
      padding: theme.isDark ? 8 : [4, 0],
      backgroundColor: theme.isDark ? 'rgba(255, 255, 255, 0.9)' : '',
      borderRadius: 2,
      display: 'inline-flex',
      gap: 5,
      alignItems: 'center',
      marginBottom: 8,
      [`@media only screen and (max-width: ${(_a = theme.breakpoints) === null || _a === void 0 ? void 0 : _a.xxs.max})`]: {
        width: '100%',
        padding: theme.isDark ? 4 : [4, 0]
      }
    }
  };
});
const useStyles$7 = createUseStyles(theme => {
  var _a;

  const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
  const breakpointXsMin = `@media only screen and (min-width: ${theme.breakpoints.xs.min})`;
  return {
    inputArea: {
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontWeight: theme.fontWeight.medium,
      fontSize: '18px',
      [breakpointMedium]: {
        fontSize: '16px'
      },
      lineHeight: '1.15em',
      padding: '6px 60px 6px 6px',
      maxWidth: '100%',
      [breakpointXsMin]: {
        // extra 66px to account for the padding
        width: ({
          cols
        }) => `calc(${cols}ch + 66px)`
      },
      width: '100%',
      border: `1px solid ${theme.answerColor}`,
      borderRadius: '0px',
      backgroundColor: '#fff',
      color: '#000',
      transition: 'all 0.1s linear',
      verticalAlign: 'top',
      textSizeAdjust: 'auto',
      '&:focus, &:hover': {
        outline: `2px solid ${theme.primaryAccentColor}`
      },
      '&:read-only:not(:disabled)': {
        borderColor: 'transparent',
        backgroundColor: theme.input.bgColor,
        color: theme.isDark ? '#fff' : '#000',
        opacity: 0.5
      },
      '&:disabled': {
        opacity: 0.4
      }
    }
  };
});

function TextArea({
  className,
  disabled = false,
  required = false,
  readOnly = false,
  cols = 50,
  ...props
}, forwardedRef) {
  const {
    inputArea
  } = useStyles$7({
    cols
  });
  return React__default.createElement("textarea", {
    className: classNames(inputArea, className),
    disabled: disabled,
    "aria-disabled": disabled,
    required: required,
    "aria-required": required,
    readOnly: readOnly,
    "aria-readonly": readOnly,
    spellCheck: true,
    ref: forwardedRef,
    ...props
  });
}

var CommentBox = forwardRef(TextArea);
const useStyles$6 = createUseStyles(theme => {
  var _a;

  const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
  const breakpointXsMin = `@media only screen and (min-width: ${theme.breakpoints.xs.min})`;
  return {
    inputField: {
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontSize: '18px',
      [breakpointMedium]: {
        fontSize: '16px'
      },
      fontWeight: theme.fontWeight.medium,
      lineHeight: '1.5em',
      height: ({
        autoHeight
      }) => autoHeight ? {} : '50px',
      maxWidth: '100%',
      width: '100%',
      [breakpointXsMin]: {
        // extra 12px to account for the padding
        width: ({
          size
        }) => `calc(${size}ch + 12px)`
      },
      padding: '6px',
      border: `1px solid ${theme.answerColor}`,
      borderRadius: '0px',
      backgroundColor: '#fff',
      color: '#000',
      transition: 'all 0.1s linear',
      '&:focus, &:hover': {
        outline: `2px solid ${theme.primaryAccentColor}`
      },
      '&:read-only:not(:disabled)': {
        borderColor: 'transparent',
        backgroundColor: theme.input.bgColor,
        color: theme.isDark ? '#fff' : '#000',
        opacity: 0.5
      },
      '&:disabled': {
        opacity: 0.4
      }
    }
  };
});

function TextInput({
  className,
  autoHeight = false,
  size = 50,
  required = false,
  disabled = false,
  readOnly = false,
  onChange: handleChange,
  ...props
}, forwardedRef) {
  const {
    inputField
  } = useStyles$6({
    autoHeight,
    size
  });
  return React__default.createElement("input", {
    type: "text",
    className: classNames(inputField, className),
    disabled: disabled,
    "aria-disabled": disabled,
    required: required,
    "aria-required": required,
    readOnly: readOnly,
    "aria-readonly": readOnly,
    spellCheck: true,
    onChange: handleChange,
    ...props,
    ref: forwardedRef
  });
}

var SingleTextbox = forwardRef(TextInput);
createUseStyles(theme => {
  return {
    commentLabelText: { ...theme.questionBody,
      marginBottom: 5,
      lineHeight: 1.25
    },
    commentLabel: {
      display: 'block'
    }
  };
});
const useStyles$5 = createUseStyles(theme => {
  var _a; // Quiz results uses the default font size


  const defaultFontSize = theme.fontSize.body;
  const {
    fontFamily = 'inherit',
    fontWeight = 'inherit',
    fontStyle = 'inherit',
    textDecoration = 'inherit',
    highlightColor = 'inherit',
    color = 'inherit'
  } = (_a = theme.questionBody) !== null && _a !== void 0 ? _a : {};
  return {
    componentContainer: {
      position: 'relative'
    },
    labelContainer: {
      display: 'inline-block',
      position: 'relative',
      padding: 0,
      margin: [4, 2],
      borderRadius: 4
    },
    textContainer: {
      margin: [3, 0, 2],
      wordBreak: 'break-word',
      pointerEvents: 'none',
      fontFamily,
      fontWeight,
      fontStyle,
      textDecoration,
      highlightColor,
      color,
      fontSize: defaultFontSize
    },
    textInputContainer: {
      margin: [5, 0, 0, 0]
    }
  };
});

function QuizResultComment({
  optionText,
  textInputValue = '',
  textInputSize = 50,
  textInputParagraphLines = 1,
  id
}) {
  const {
    componentContainer,
    labelContainer,
    textContainer,
    textInputContainer
  } = useStyles$5();
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    className: componentContainer
  }, React__default.createElement("label", {
    htmlFor: id,
    className: labelContainer
  }, React__default.createElement("div", {
    className: textContainer
  }, React__default.createElement(RichText, {
    element: "span",
    text: optionText
  })))), React__default.createElement("div", {
    className: textInputContainer
  }, textInputParagraphLines <= 1 ? React__default.createElement(SingleTextbox, {
    id: id,
    name: "otherOptionText",
    value: textInputValue,
    size: textInputSize,
    autoHeight: true,
    readOnly: true
  }) : React__default.createElement(CommentBox, {
    id: id,
    name: "otherOptionText",
    rows: textInputParagraphLines,
    defaultValue: textInputValue,
    cols: textInputSize,
    translate: "no",
    readOnly: true
  })));
}

var useStyles$4 = createUseStyles({
  srOnly: visuallyHidden
});
const COPY$1 = defineMessages({
  CORRECT: {
    id: 'SrText.CORRECT',
    defaultMessage: 'Correct',
    description: '[Type: label][Vis: med] - the answer was correct'
  },
  INCORRECT: {
    id: 'SrText.INCORRECT',
    defaultMessage: 'Incorrect',
    description: '[Type: label][Vis: med] - the answer was incorrect '
  }
});

function SrText({
  icon
}) {
  const {
    srOnly
  } = useStyles$4();
  return React__default.createElement("div", {
    className: srOnly
  }, icon ? React__default.createElement(T, {
    desc: icon === 'correct' ? COPY$1.CORRECT : COPY$1.INCORRECT
  }) : null);
}

function QuizCorrectIcon({
  isAriaHidden = false
}) {
  return React__default.createElement("svg", {
    width: "14px",
    height: "10px",
    viewBox: "0 0 14 10",
    version: "1.1",
    "data-testid": "quiz-result-correct-icon",
    "aria-hidden": isAriaHidden
  }, React__default.createElement("g", {
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("g", {
    transform: "translate(-257, -338.75)",
    fill: "#F2FAE8"
  }, React__default.createElement("g", {
    id: "check-correct",
    transform: "translate(254, 334)"
  }, React__default.createElement("g", null, React__default.createElement("path", {
    d: "M4,10.3259737 C4,10.4757908 4.04494448,10.5956428 4.13483478,10.6855331 L7.33716071,13.8990952 C7.42705101,13.9889855 7.55626637,14.069511 7.72481069,14.1406742 C7.893355,14.2118373 8.04504261,14.2474184 8.17987806,14.2474184 L8.78663455,14.2474184 C9.07877802,14.2474184 9.359681,14.1313118 9.6293519,13.8990952 L16.4160356,7.08993903 C16.5059259,7.00004873 16.5508704,6.88206948 16.5508704,6.73599774 C16.5508704,6.589926 16.5059259,6.47194675 16.4160356,6.38205645 L15.1575777,5.13483478 C15.0676874,5.04494448 14.9515809,5 14.8092545,5 C14.6669282,5 14.5433309,5.04494448 14.4384589,5.13483478 L8.85405194,10.7529504 C8.76416164,10.8428407 8.6443097,10.8877852 8.49449253,10.8877852 C8.34467537,10.8877852 8.22482343,10.8428407 8.13493313,10.7529504 L6.11241149,8.7304288 C6.02252119,8.6405385 5.90266926,8.59559403 5.75285209,8.59559403 C5.60303492,8.59559403 5.48318299,8.6405385 5.39329269,8.7304288 L4.13483478,9.96641425 C4.04494448,10.0563045 4,10.1761565 4,10.3259737 L4,10.3259737 Z"
  }))))));
}

function QuizWrongIcon({
  isAriaHidden = false
}) {
  return React__default.createElement("svg", {
    width: "12px",
    height: "12px",
    viewBox: "0 0 12 12",
    version: "1.1",
    "data-testid": "quiz-result-wrong-icon",
    "aria-hidden": isAriaHidden
  }, React__default.createElement("g", {
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, React__default.createElement("path", {
    d: "M0.281690141,9.92648775 C0.281690141,10.187088 0.368074254,10.4068447 0.54084507,10.5857643 L1.25070423,11.3208868 C1.42347504,11.4998064 1.63567949,11.5892649 1.88732394,11.5892649 C2.13896839,11.5892649 2.35117285,11.4998064 2.52394366,11.3208868 L5.03661972,8.71878646 C5.20939054,8.53986686 5.42159499,8.4504084 5.67323944,8.4504084 C5.92488389,8.4504084 6.1446,8.53986686 6.33239437,8.71878646 L8.82253521,11.3208868 C8.99530603,11.4998064 9.20751048,11.5892649 9.45915493,11.5892649 C9.71079938,11.5892649 9.93051549,11.4998064 10.1183099,11.3208868 L10.828169,10.5857643 C11.0009398,10.4068447 11.0873239,10.187088 11.0873239,9.92648775 C11.0873239,9.66588746 11.0009398,9.43835181 10.828169,9.24387398 L8.31549296,6.66511085 C8.14272214,6.47063302 8.05633803,6.24698687 8.05633803,5.99416569 C8.05633803,5.74134452 8.14272214,5.51769837 8.31549296,5.32322054 L10.828169,2.74445741 C11.0009398,2.54997958 11.0873239,2.32244393 11.0873239,2.06184364 C11.0873239,1.80124335 11.0009398,1.5814867 10.828169,1.40256709 L10.1183099,0.667444574 C9.93051549,0.488524971 9.71079938,0.399066511 9.45915493,0.399066511 C9.20751048,0.399066511 8.99530603,0.488524971 8.82253521,0.667444574 L6.33239437,3.26954492 C6.1446,3.44846453 5.92488389,3.53792299 5.67323944,3.53792299 C5.42159499,3.53792299 5.20939054,3.44846453 5.03661972,3.26954492 L2.52394366,0.667444574 C2.35117285,0.488524971 2.13896839,0.399066511 1.88732394,0.399066511 C1.63567949,0.399066511 1.42347504,0.488524971 1.25070423,0.667444574 L0.54084507,1.40256709 C0.368074254,1.5814867 0.281690141,1.80124335 0.281690141,2.06184364 C0.281690141,2.32244393 0.368074254,2.54997958 0.54084507,2.74445741 L3.05352113,5.32322054 C3.22629194,5.51769837 3.31267606,5.74134452 3.31267606,5.99416569 C3.31267606,6.24698687 3.22629194,6.47063302 3.05352113,6.66511085 L0.54084507,9.24387398 C0.368074254,9.43835181 0.281690141,9.66588746 0.281690141,9.92648775 L0.281690141,9.92648775 Z",
    fill: "#FFFFFF"
  })));
}

const icons = {
  correct: QuizCorrectIcon,
  wrong: QuizWrongIcon
};
const useStyles$3 = createUseStyles(theme => {
  const size = {
    height: 20,
    width: 20
  };
  const containerBase = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...size
  };

  const getIconContainerBg = (highlight, backgroundColor, iconName) => {
    if (iconName === 'correct') {
      return '#00BF6F';
    }

    if (iconName === 'wrong') {
      return '#b32e22';
    }

    if (highlight) {
      return theme.correctAnswerHighlightColor;
    }

    return 'transparent';
  };

  const getIconContainerBorderColor = iconName => {
    if (iconName === 'correct') {
      return theme.border.correctColor;
    }

    if (iconName === 'wrong') {
      return theme.border.wrongColor;
    }

    return 'inherit';
  };

  const getIconContainerBackground = (highlight, backgroundColor) => {
    if (!backgroundColor) {
      return 'inherit';
    }

    if (highlight) {
      return theme.primaryBackgroundColor;
    }

    return backgroundColor;
  };

  return {
    radioContainer: {
      position: 'relative',
      ...size
    },
    radioInput: {
      opacity: 0,
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%'
    },
    iconContainer: ({
      highlight,
      iconName,
      type,
      backgroundColor
    }) => ({ ...containerBase,
      position: 'relative',
      borderRadius: type === 'radio' ? 10 : 2,
      height: iconName ? size.height : size.height - 2,
      border: iconName ? ['1px', 'solid', theme.border.defaultColor] : [],
      backgroundColor: getIconContainerBg(highlight, backgroundColor, iconName),
      borderColor: getIconContainerBorderColor(iconName)
    }),
    // container needed as background for alpha transparency in `correctAnswerHighlightColor`
    iconContainerBackground: ({
      highlight,
      type,
      iconName,
      backgroundColor
    }) => ({ ...containerBase,
      position: 'absolute',
      borderRadius: type === 'radio' ? 10 : 2,
      border: iconName ? [] : ['1px', 'solid', theme.border.defaultColor],
      backgroundColor: getIconContainerBackground(highlight, backgroundColor)
    })
  };
});

function InputIcon({
  type,
  id,
  ariaLabelledby,
  highlight = false,
  checked,
  isCorrect = true,
  ariaDescribedBy = [],
  noScreenReaderText = false,
  backgroundColor
}) {
  let iconName;

  if (checked && isCorrect) {
    iconName = 'correct';
  } else if (checked) {
    iconName = 'wrong';
  }

  const {
    radioContainer,
    radioInput,
    iconContainer,
    iconContainerBackground
  } = useStyles$3({
    highlight,
    iconName,
    type,
    backgroundColor
  });
  const Icon = iconName && icons[iconName];
  return React__default.createElement("div", {
    className: radioContainer
  }, React__default.createElement("input", {
    id: id,
    className: radioInput,
    type: type,
    checked: !!checked,
    "aria-labelledby": ariaLabelledby || undefined,
    "aria-describedby": ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined,
    readOnly: true
  }), React__default.createElement("div", {
    className: iconContainerBackground
  }, React__default.createElement("div", {
    className: iconContainer,
    "data-testid": "quiz-results-icon-container"
  }, Icon && React__default.createElement(Icon, {
    isAriaHidden: true
  }))), iconName && !noScreenReaderText && React__default.createElement(SrText, {
    icon: iconName
  }));
}

const useStyles$2 = createUseStyles(theme => {
  var _a; // Quiz results uses the default font size


  const defaultFrontSize = theme.fontSize.body;
  const {
    fontFamily = 'inherit',
    fontWeight = 'inherit',
    fontStyle = 'inherit',
    textDecoration = 'inherit',
    highlightColor = 'inherit',
    color = 'inherit'
  } = (_a = theme.questionBody) !== null && _a !== void 0 ? _a : {};
  return {
    componentContainer: {
      position: 'relative'
    },
    radioInput: {
      opacity: 0,
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%'
    },
    labelContainer: ({
      highlight,
      type
    }) => ({
      display: 'inline-block',
      position: 'relative',
      padding: [1, 8],
      backgroundColor: highlight ? theme.correctAnswerHighlightColor : 'initial',
      borderRadius: type === 'checkbox' ? 4 : 13,
      // fix for IE11 bug - images leaking out of label container
      boxSizing: 'border-box',
      maxWidth: '100%'
    }),
    textContainer: ({
      highlight
    }) => {
      var _a;

      return {
        /* Right and left margin are both defined to account for RTL direction styling. */
        margin: [3, 30, 2],
        wordBreak: 'break-word',
        fontFamily,
        fontWeight,
        fontStyle,
        textDecoration,
        highlightColor,
        color,
        ...(highlight ? {
          fontWeight: (_a = theme.fontWeight.medium) !== null && _a !== void 0 ? _a : {}
        } : {}),
        fontSize: defaultFrontSize
      };
    },
    iconContainer: {
      margin: 2,
      position: 'absolute'
    },
    textInputContainer: {
      margin: [5, 0, 0, 0]
    },
    [`@media (min-width: ${theme.breakpoints.sm.min})`]: {
      textInputContainer: {
        margin: [5, 0, 0, 32]
      }
    }
  };
});

function QuizResultChoices({
  id,
  type,
  optionText,
  highlight = false,
  icon,
  hasTextInput = false,
  textInputValue = '',
  textInputSize = 50,
  textInputParagraphLines = 1
}) {
  const {
    componentContainer,
    labelContainer,
    textContainer,
    iconContainer,
    textInputContainer
  } = useStyles$2({
    highlight,
    icon,
    type
  });
  const uniqId = useMemo(() => uniqueId('id-'), []);
  const radioButtonId = id || uniqId;
  const textInputId = `caption-${radioButtonId}`;
  const inputId = `input-${radioButtonId}`;
  const ariaDescribedBy = [];

  if (hasTextInput) {
    ariaDescribedBy.push(textInputId);
  }

  return React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    className: componentContainer
  }, React__default.createElement("label", {
    htmlFor: inputId,
    className: labelContainer
  }, React__default.createElement("div", {
    className: iconContainer
  }, React__default.createElement(InputIcon, {
    type: type,
    id: inputId,
    checked: !!icon,
    isCorrect: icon === 'correct',
    highlight: highlight,
    ariaDescribedBy: ariaDescribedBy,
    noScreenReaderText: true
  })), React__default.createElement("div", {
    className: textContainer,
    translate: "no"
  }, React__default.createElement(RichText, {
    element: "span",
    text: optionText
  })), React__default.createElement(SrText, {
    icon: icon
  }))), React__default.createElement("div", {
    className: textInputContainer,
    translate: "no"
  }, hasTextInput && (textInputParagraphLines <= 1 ? React__default.createElement(SingleTextbox, {
    id: textInputId,
    name: "otherOptionText",
    value: textInputValue,
    size: textInputSize,
    "aria-labelledby": inputId,
    autoHeight: true,
    readOnly: true
  }) : React__default.createElement(CommentBox, {
    id: textInputId,
    name: "otherOptionText",
    rows: textInputParagraphLines,
    defaultValue: textInputValue,
    cols: textInputSize,
    "aria-labelledby": inputId,
    readOnly: true
  }))));
}

const useStyles$1 = createUseStyles(theme => {
  return {
    imageChoice: {
      display: 'inline-block'
    },
    componentContainer: ({
      highlight
    }) => ({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      alignItems: 'center',
      width: 224,
      borderRadius: 2,
      border: highlight ? `2px solid ${theme.answerColor}` : '1px solid #D0D2D3'
    }),
    iconLabel: {
      position: 'absolute',
      zIndex: 1,
      top: 2,
      left: 2
    },
    imageContainer: ({
      highlight
    }) => ({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: 168,
      position: 'relative',
      backgroundColor: highlight ? theme.correctAnswerHighlightColor : 'rgb(239, 239, 239)'
    }),
    imageStyle: {
      maxHeight: '100%',
      maxWidth: '100%',
      height: 'auto',
      width: 'auto',
      pointerEvents: 'none',
      flex: 'none' // fix for IE 11 - images leaking out of container

    },
    captionStyle: ({
      highlight
    }) => {
      var _a;

      return {
        fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
        display: 'block',
        backgroundColor: highlight ? theme.correctAnswerHighlightColor : 'initial',
        color: theme.answerColor,
        width: '100%',
        padding: [7, 9, 9, 7],
        fontWeight: highlight ? 500 : 'initial'
      };
    },
    srOnly: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: 0
    }
  };
});

function QuizResultImageChoice({
  id,
  highlight = false,
  selected = false,
  image,
  caption = '',
  correctMessage,
  selectedMessage,
  isManyAnswers = false
}) {
  const {
    imageChoice,
    componentContainer,
    imageContainer,
    imageStyle,
    iconLabel,
    captionStyle,
    srOnly
  } = useStyles$1({
    highlight
  });
  const uniqId = useMemo(() => uniqueId('id-'), []);
  const imageChoiceId = id || uniqId;
  const captionId = `caption-${imageChoiceId}`;
  const selectedId = `selected-${imageChoiceId}`;
  const inputId = `input-${imageChoiceId}`;
  const highlightedId = `highlighted-${imageChoiceId}`;
  const ariaDescribedBy = [];

  if (highlight) {
    ariaDescribedBy.push(highlightedId);
  }

  if (selected) {
    ariaDescribedBy.push(selectedId);
  }

  return React__default.createElement("div", {
    className: imageChoice
  }, React__default.createElement("div", {
    className: componentContainer
  }, React__default.createElement("div", {
    className: imageContainer
  }, React__default.createElement("label", {
    htmlFor: inputId,
    className: iconLabel
  }, React__default.createElement(InputIcon, {
    id: inputId,
    type: isManyAnswers ? 'checkbox' : 'radio',
    checked: selected,
    isCorrect: selected && highlight,
    highlight: highlight,
    backgroundColor: "rgb(239, 239, 239)",
    ariaDescribedBy: ariaDescribedBy,
    ariaLabelledby: caption ? `${captionId} ${inputId}` : undefined
  })), React__default.createElement("img", {
    className: imageStyle,
    src: image.url,
    // Only add alt text if it's different than the label that describes it
    alt: caption === image.altText ? '' : image.altText,
    "aria-labelledby": caption || undefined,
    "aria-describedby": ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined
  })), caption && React__default.createElement("span", {
    className: captionStyle,
    id: captionId,
    translate: "no"
  }, caption), highlight && React__default.createElement("span", {
    className: srOnly,
    id: highlightedId
  }, correctMessage), selected && React__default.createElement("span", {
    className: srOnly,
    id: selectedId
  }, selectedMessage)));
}

const useStyles = createUseStyles(theme => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j; // Quiz results has a consistent font size


  const fontSize = theme.fontSize.body;
  return {
    componentContainer: {
      margin: [5, 0],
      fontFamily: (_b = (_a = theme.questionTitle) === null || _a === void 0 ? void 0 : _a.fontFamily) !== null && _b !== void 0 ? _b : {},
      fontStyle: (_d = (_c = theme.questionTitle) === null || _c === void 0 ? void 0 : _c.fontStyle) !== null && _d !== void 0 ? _d : {},
      fontWeight: (_f = (_e = theme.questionTitle) === null || _e === void 0 ? void 0 : _e.fontWeight) !== null && _f !== void 0 ? _f : {},
      color: ((_g = theme.questionTitle) === null || _g === void 0 ? void 0 : _g.color) || theme.questionColor,
      fontSize,
      textDecoration: (_j = (_h = theme.questionTitle) === null || _h === void 0 ? void 0 : _h.textDecoration) !== null && _j !== void 0 ? _j : {}
    },
    scoreContainer: {
      display: 'inline'
    },
    feedbackContainer: {
      marginTop: 8,
      marginBottom: 0,
      wordWrap: 'wrap'
    },
    skippedBadge: {
      display: 'inline',
      padding: [2, 15],
      borderRadius: 15,
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
      marginLeft: 5
    }
  };
});
const COPY = defineMessages({
  SCORE: {
    id: 'QuizResultScore.SCORE',
    defaultMessage: '{score}/{total} {total, plural, one {point} other {points}}',
    description: '[Type: header][Vis: med] - total achieved point(s) of this quiz question'
  },
  SKIPPED: {
    id: 'QuizResultScore.SKIPPED',
    defaultMessage: 'Skipped',
    description: '[Type: header][Vis: med] - user skipped this quiz question'
  }
});

function QuizResultScore({
  score,
  total,
  feedback,
  skipped = false
}) {
  const {
    componentContainer,
    scoreContainer,
    feedbackContainer,
    skippedBadge
  } = useStyles();
  return React__default.createElement("div", {
    className: componentContainer
  }, React__default.createElement("p", {
    className: scoreContainer
  }, React__default.createElement(T, {
    desc: COPY.SCORE,
    values: {
      score,
      total
    }
  })), skipped && React__default.createElement("span", {
    className: skippedBadge
  }, React__default.createElement(T, {
    desc: COPY.SKIPPED
  })), feedback && React__default.createElement("p", {
    translate: "no",
    className: feedbackContainer,
    dangerouslySetInnerHTML: {
      __html: sanitizeString(unescaper(feedback))
    }
  }));
}
/**
 * Get the icon to indicator whether or not user got the answer correct or not
 * @param responded whether user selected the answer option or not
 * @param answerOptionPoints answer option points
 */


const getCorrectnessIndicatorIcon = (responded, answerOptionPoints) => {
  if (!responded) {
    return undefined;
  }

  if (answerOptionPoints <= 0) {
    return 'wrong';
  }

  return 'correct';
};
/** Gets the icon and highlight for `idToSearch` */


const getIconAndHighlight = (answers, idToSearch, highlightCorrectAnswers) => {
  const {
    points = 0,
    responded = false
  } = answers.find(({
    answer
  }) => answer.id === idToSearch) || {};
  const icon = getCorrectnessIndicatorIcon(responded, points);
  return {
    icon,
    highlight: highlightCorrectAnswers && points > 0
  };
};
/** Picks feedback string based on user/total score */


const pickFeedback = (feedback, userScore, totalScore) => {
  if (userScore >= totalScore) {
    return feedback.correct;
  }

  if (userScore > 0) {
    return feedback.partial;
  }

  return feedback.incorrect;
};
/**
 * Calculate user score for a quiz question
 * @param quizAnswers Quiz or QuizImage results answers
 */


const getUserScore = quizAnswers => {
  return quizAnswers.reduce((acc, cur) => {
    return cur.responded ? acc + cur.points : acc;
  }, 0);
};
/** Helper Type Guard to filter our `undefined` */


const isNotUndefined = a => a !== undefined;

const isPureQuizAnswer = answer => 'label' in answer.answer && 'visible' in answer.answer;
/** Filter `answers` to only regular Answers without invisible, "other" and "none of the above" */


const filterQuizAnswers = (answers, idsToFilter) => answers.filter(isPureQuizAnswer).filter(({
  answer: {
    visible
  }
}) => visible).filter(a => !idsToFilter.filter(isNotUndefined).includes(a.answer.id));

const isPureQuizImageAnswer = answer => 'label' in answer.answer && 'visible' in answer.answer && 'image' in answer.answer;
/** QuizImageAnswer without "other" and "none of the above" */


const filterOtherFromQuizImageAnswer = (answers, idsToFilter) => answers.filter(isPureQuizImageAnswer).filter(({
  answer: {
    visible
  }
}) => visible).filter(a => !idsToFilter.filter(isNotUndefined).includes(a.answer.id));

function CheckBox({
  id,
  title,
  questionNumber,
  numberingEnabled,
  highlightCorrectAnswers,
  answerNoneOfTheAbove,
  answerLayoutColumns,
  spacing,
  answerOther,
  quizResults
}) {
  const userScore = getUserScore(quizResults.answers);
  const totalScore = quizResults.totalPoints;
  const feedback = pickFeedback(quizResults.feedback, userScore, totalScore);
  /** Visible answers without "other" and "none of the above" */

  const regularAnswers = filterQuizAnswers(quizResults.answers, [answerNoneOfTheAbove === null || answerNoneOfTheAbove === void 0 ? void 0 : answerNoneOfTheAbove.id, answerOther === null || answerOther === void 0 ? void 0 : answerOther.id]);
  const isComment = (answerOther === null || answerOther === void 0 ? void 0 : answerOther.type) === 'COMMENT'; // eslint-disable-next-line react/no-unstable-nested-components

  function NoneOfTheAboveOption() {
    return (answerNoneOfTheAbove === null || answerNoneOfTheAbove === void 0 ? void 0 : answerNoneOfTheAbove.visible) ? React__default.createElement(QuizResultChoices, {
      id: answerNoneOfTheAbove.id,
      type: "checkbox",
      optionText: answerNoneOfTheAbove.label,
      textInputValue: "",
      ...getIconAndHighlight(quizResults.answers, answerNoneOfTheAbove.id, highlightCorrectAnswers)
    }) : null;
  } // eslint-disable-next-line react/no-unstable-nested-components


  function OtherOption() {
    var _a, _b;

    if (!(answerOther === null || answerOther === void 0 ? void 0 : answerOther.visible)) {
      return null;
    }

    return isComment ? React__default.createElement(QuizResultComment, {
      id: answerOther.id,
      optionText: answerOther.label,
      textInputValue: ((_a = quizResults.answerOther) === null || _a === void 0 ? void 0 : _a.textAnswer) || '',
      textInputSize: answerOther.charsCount,
      textInputParagraphLines: answerOther.linesCount
    }) : React__default.createElement(QuizResultChoices, {
      id: answerOther.id,
      type: "checkbox",
      optionText: answerOther.label,
      textInputValue: ((_b = quizResults.answerOther) === null || _b === void 0 ? void 0 : _b.textAnswer) || '',
      hasTextInput: true,
      textInputSize: answerOther.charsCount,
      textInputParagraphLines: answerOther.linesCount,
      ...getIconAndHighlight(quizResults.answers, answerOther.id, !isComment && highlightCorrectAnswers)
    });
  }

  return React__default.createElement(QuestionFieldLayoutTemplate, {
    title: React__default.createElement(RespondentQuestionTitle, {
      id: id,
      heading: title,
      required: false,
      numbered: numberingEnabled,
      number: parseInt(questionNumber, 10),
      useDefaultFrontSize: true
    }),
    footer: React__default.createElement(QuizResultScore, {
      score: userScore,
      total: totalScore,
      feedback: feedback,
      skipped: false
    }),
    padding: spacing,
    clickShield: false,
    hasFooterPadding: true
  }, React__default.createElement(QuestionAnswerLayoutTemplate, {
    gridCellMargin: [4, 2],
    columns: answerLayoutColumns,
    noneOfTheAbove: !isComment ? React__default.createElement(NoneOfTheAboveOption, null) : null,
    other: React__default.createElement(OtherOption, null)
  }, regularAnswers.map(({
    answer,
    responded,
    points
  }) => {
    return React__default.createElement(QuizResultChoices, {
      key: answer.id,
      textInputValue: "",
      id: answer.id,
      type: "checkbox",
      optionText: answer.label,
      icon: getCorrectnessIndicatorIcon(responded, points),
      // Correct answers are expected to have a point value above 0
      // and should be highlighted.
      highlight: highlightCorrectAnswers && points > 0
    });
  }).concat(isComment ? [React__default.createElement(NoneOfTheAboveOption, {
    key: answerNoneOfTheAbove === null || answerNoneOfTheAbove === void 0 ? void 0 : answerNoneOfTheAbove.id
  })] : [])));
}

var CheckBox$1 = withErrorBoundary(CheckBox);

function MultipleChoice({
  id,
  title,
  questionNumber,
  numberingEnabled,
  highlightCorrectAnswers,
  answerNoneOfTheAbove,
  answerLayoutColumns,
  spacing,
  answerOther,
  quizResults
}) {
  const userScore = getUserScore(quizResults.answers);
  const totalScore = quizResults.totalPoints;
  const feedback = pickFeedback(quizResults.feedback, userScore, totalScore);
  /** Visible answers without "other" and "none of the above" */

  const regularAnswers = filterQuizAnswers(quizResults.answers, [answerNoneOfTheAbove === null || answerNoneOfTheAbove === void 0 ? void 0 : answerNoneOfTheAbove.id, answerOther === null || answerOther === void 0 ? void 0 : answerOther.id]);
  const isComment = (answerOther === null || answerOther === void 0 ? void 0 : answerOther.type) === 'COMMENT'; // eslint-disable-next-line react/no-unstable-nested-components

  function NoneOfTheAboveOption() {
    return (answerNoneOfTheAbove === null || answerNoneOfTheAbove === void 0 ? void 0 : answerNoneOfTheAbove.visible) ? React__default.createElement(QuizResultChoices, {
      type: "radio",
      id: answerNoneOfTheAbove.id,
      optionText: answerNoneOfTheAbove.label,
      textInputValue: "",
      ...getIconAndHighlight(quizResults.answers, answerNoneOfTheAbove.id, highlightCorrectAnswers)
    }) : null;
  } // eslint-disable-next-line react/no-unstable-nested-components


  function OtherOption() {
    var _a, _b;

    if (!(answerOther === null || answerOther === void 0 ? void 0 : answerOther.visible)) {
      return null;
    }

    return isComment ? React__default.createElement(QuizResultComment, {
      id: answerOther.id,
      optionText: answerOther.label,
      textInputValue: ((_a = quizResults.answerOther) === null || _a === void 0 ? void 0 : _a.textAnswer) || '',
      textInputSize: answerOther.charsCount,
      textInputParagraphLines: answerOther.linesCount
    }) : React__default.createElement(QuizResultChoices, {
      type: "radio",
      id: answerOther.id,
      optionText: answerOther.label,
      textInputValue: ((_b = quizResults.answerOther) === null || _b === void 0 ? void 0 : _b.textAnswer) || '',
      hasTextInput: true,
      textInputSize: answerOther.charsCount,
      textInputParagraphLines: answerOther.linesCount,
      ...getIconAndHighlight(quizResults.answers, answerOther.id, !isComment && highlightCorrectAnswers)
    });
  }

  return React__default.createElement(QuestionFieldLayoutTemplate, {
    title: React__default.createElement(RespondentQuestionTitle, {
      id: id,
      heading: title,
      required: false,
      numbered: numberingEnabled,
      number: parseInt(questionNumber, 10),
      useDefaultFrontSize: true
    }),
    footer: React__default.createElement(QuizResultScore, {
      score: userScore,
      total: totalScore,
      feedback: feedback,
      skipped: false
    }),
    padding: spacing,
    clickShield: false,
    hasFooterPadding: true
  }, React__default.createElement(QuestionAnswerLayoutTemplate, {
    gridCellMargin: [4, 2],
    columns: answerLayoutColumns,
    noneOfTheAbove: !isComment ? React__default.createElement(NoneOfTheAboveOption, null) : null,
    other: React__default.createElement(OtherOption, null)
  }, regularAnswers.map(answerOption => {
    const {
      answer,
      points,
      responded
    } = answerOption;
    const icon = getCorrectnessIndicatorIcon(responded, points);
    return React__default.createElement(QuizResultChoices, {
      type: "radio",
      key: answer.id,
      id: answer.id,
      optionText: answer.label,
      textInputValue: "",
      icon: icon,
      // Correct answers are expected to have a point value above 0
      // and should be highlighted.
      highlight: highlightCorrectAnswers && points > 0
    });
  }).concat(isComment && answerNoneOfTheAbove ? [React__default.createElement(NoneOfTheAboveOption, {
    key: answerNoneOfTheAbove.id
  })] : [])));
}

var MultipleChoice$1 = withErrorBoundary(MultipleChoice);

function ImageChoice({
  id,
  title,
  questionNumber,
  highlightCorrectAnswers,
  numberingEnabled,
  answerNoneOfTheAbove,
  quizResults,
  isManyAnswers
}) {
  const userScore = getUserScore(quizResults.answers);
  const totalScore = quizResults.totalPoints;
  const feedback = pickFeedback(quizResults.feedback, userScore, totalScore);
  const noneOfTheAboveOption = answerNoneOfTheAbove && answerNoneOfTheAbove.visible ? React__default.createElement(QuizResultChoices, {
    type: isManyAnswers ? 'checkbox' : 'radio',
    id: answerNoneOfTheAbove.id,
    optionText: answerNoneOfTheAbove.label,
    textInputValue: "",
    ...getIconAndHighlight(quizResults.answers, answerNoneOfTheAbove.id, highlightCorrectAnswers)
  }) : null;
  /** Answers without "none of the above" */

  const regularAnswers = filterOtherFromQuizImageAnswer(quizResults.answers, [answerNoneOfTheAbove === null || answerNoneOfTheAbove === void 0 ? void 0 : answerNoneOfTheAbove.id]);
  return React__default.createElement(QuestionFieldLayoutTemplate, {
    title: React__default.createElement(RespondentQuestionTitle, {
      id: id,
      heading: title,
      required: false,
      numbered: numberingEnabled,
      number: parseInt(questionNumber, 10),
      useDefaultFrontSize: true
    }),
    footer: React__default.createElement(QuizResultScore, {
      score: userScore,
      total: totalScore,
      feedback: feedback,
      skipped: false
    }),
    clickShield: false,
    hasFooterPadding: true
  }, React__default.createElement(QuestionAnswerLayoutTemplate, {
    columns: "horizontal",
    noneOfTheAbove: noneOfTheAboveOption,
    adjustToContent: true,
    otherCellMargin: [4, 2, 9, 2],
    gridCellMargin: [15, 24, 15, 0]
  }, regularAnswers.map(answerOption => {
    const {
      answer,
      points,
      responded
    } = answerOption;
    return React__default.createElement(QuizResultImageChoice, {
      key: answer.id,
      id: answer.id,
      image: answer.image,
      caption: answer.label,
      selected: responded,
      isManyAnswers: isManyAnswers,
      // Correct answers are expected to have a point value above 0
      // and should be highlighted.
      highlight: highlightCorrectAnswers && points > 0
    });
  })));
}

var ImageChoice$1 = withErrorBoundary(ImageChoice);
export { CheckBox$1 as CheckBox, ImageChoice$1 as ImageChoice, MultipleChoice$1 as MultipleChoice };